import { UndefinedInitialDataOptions, useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { authActions } from '../requests/authRequest';
import { HttpRequestData, HttpRequestMeta } from '../types';
import { HttpClient, HttpRequestError } from '../http';
import { viewRequests } from '../requests';
import { loginState } from '../state';
import { useAtom } from 'jotai';

interface HttpQueryOptions<TResponse> extends Omit<UndefinedInitialDataOptions<TResponse, Error>, 'queryKey'> {
    readonly queryKey?: unknown[];
    readonly noCaching?: boolean;
}

const isMobile = typeof document === 'undefined';

export const useHttpQuery = <TRequest, TResponse>(
    requestMeta: HttpRequestMeta<TRequest, TResponse>,
    requestData?: HttpRequestData,
    options?: HttpQueryOptions<TResponse>) => {

    const [loginInfo] = useAtom(loginState);

    const baseUrl = requestMeta.baseUrl();

    if (!baseUrl) {
        throw new Error('Base URL is null');
    }

    const queryKey: unknown[] = [requestMeta.method, baseUrl, requestMeta.path, { ...requestData?.query }, { ...requestData?.pathData }];

    const timestampRef = useRef(options?.noCaching && Date.now());
    if (options?.noCaching) {
        queryKey.push(timestampRef.current);
    }

    const query = useQuery<TResponse, Error>({
        ...options,
        placeholderData: (previousData) => previousData,
        queryKey,
        queryFn: async (): Promise<TResponse> => {
            const httpClient = new HttpClient();
            if (requestMeta.authentication === 'bearer') {
                const accessToken = loginInfo?.token;
                if (!!accessToken) {
                    httpClient.setAuthorization('bearer', accessToken);
                }
            }

            if (requestMeta.authentication === 'basic') {
                const accessKey = requestData?.header?.basic;
                if (!!accessKey) {
                    httpClient.setAuthorization('basic', accessKey);
                }
            }

            try {
                return await httpClient.send<TResponse>({
                    ...requestMeta,
                    ...requestData,
                });
            } catch (error) {
                if (isMobile) throw error;

                if (error instanceof Error && error.name === 'HttpRequestError') {
                    const requestError = error as HttpRequestError;

                    if (requestError.statusCode === 401) {
                        await authActions.logout.send();
                        location.href = '/error/unauthorized';
                    }

                    throw error;
                }

                if (error instanceof Error && error.message === 'Failed to fetch') {

                    viewRequests.showNotification.send({
                        type: 'error',
                        title: 'Network Error',
                        message: 'Please check your internet connection and try again.',
                    });
                    throw error;
                }

                viewRequests.showNotification.send({
                    type: 'error',
                    title: (error as Error).name,
                    message: (error as Error).message,
                });

                throw error;
            }
        },

    });

    return query;
};


